// components/formDialog/BulkUploadStudentsDialog.tsx
import { Button } from "@/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBulkUploadStudentsMutation } from "@/hooks/api/student.queries";
import type { BulkUploadDocType } from "@/services/student.service";
import {
  AlertCircle,
  Download,
  FileSpreadsheet,
  FileText,
  Link,
  X,
} from "lucide-react";
import * as XLSX from "xlsx";
import { useRef, useState } from "react";

const SAMPLE_ROWS = [
  ["full_name", "org_email", "personal_email", "roll_number", "phone"],
  [
    "Arjun Sharma",
    "arjun.sharma@college.edu",
    "arjun.sharma@gmail.com",
    "CS2024001",
    "9876543210",
  ],
  [
    "Priya Nair",
    "priya.nair@college.edu",
    "priya.nair@gmail.com",
    "CS2024002",
    "9876543211",
  ],
  [
    "Rahul Verma",
    "rahul.verma@college.edu",
    "rahul.verma@gmail.com",
    "CS2024003",
    "9876543212",
  ],
];

// CSV download
const SAMPLE_CSV = SAMPLE_ROWS.map((r) => r.join(",")).join("\n");

const downloadSampleCSV = () => {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "students_sample.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// Excel download using SheetJS
const downloadSampleExcel = () => {
  const ws = XLSX.utils.aoa_to_sheet(SAMPLE_ROWS);
  // Set column widths (chars)
  ws["!cols"] = [
    { wch: 20 },
    { wch: 30 },
    { wch: 30 },
    { wch: 14 },
    { wch: 14 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Students");
  XLSX.writeFile(wb, "students_sample.xlsx");
};

// ─── Types ─────────────────────────────────────────────────────────────────────
type DocTab = BulkUploadDocType;

const TABS: { id: DocTab; label: string; icon: React.ReactNode }[] = [
  { id: "csv", label: "CSV", icon: <FileText size={14} /> },
  { id: "xlsx", label: "Excel", icon: <FileSpreadsheet size={14} /> },
  { id: "google_sheet", label: "Google Sheet", icon: <Link size={14} /> },
];

// ─── Component ─────────────────────────────────────────────────────────────────
type Props = {
  isOpen: boolean;
  onClose: () => void;
  batchId: string;
};

const BulkUploadStudentsDialog = ({ isOpen, onClose, batchId }: Props) => {
  const [activeTab, setActiveTab] = useState<DocTab>("csv");
  const [file, setFile] = useState<File | null>(null);
  const [sheetUrl, setSheetUrl] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useBulkUploadStudentsMutation(batchId);

  const handleClose = () => {
    setFile(null);
    setSheetUrl("");
    setActiveTab("csv");
    onClose();
  };

  const handleTabChange = (tab: DocTab) => {
    setActiveTab(tab);
    setFile(null);
    setSheetUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isGoogleSheet = activeTab === "google_sheet";
  const accept = activeTab === "csv" ? ".csv" : ".xlsx,.xls";

  const canSubmit =
    !uploadMutation.isPending &&
    (isGoogleSheet ? sheetUrl.trim().length > 0 : file != null);

  const handleSubmit = () => {
    uploadMutation.mutate(
      {
        batch_id: batchId,
        doc_type: activeTab,
        file: file ?? undefined,
        sheet_url: isGoogleSheet ? sheetUrl.trim() : undefined,
      },
      {
        // Keep the dialog open if any row failed so the user can fix & retry;
        // close it when everything was created or skipped cleanly.
        onSuccess: (result) => {
          if (result.errors.length === 0) handleClose();
        },
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-lg"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Bulk Upload Students</DialogTitle>
        </DialogHeader>

        {/* Column reference */}
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
          <p className="mb-1 font-semibold">Required columns</p>
          <p>
            <code className="rounded bg-blue-100 px-1">full_name</code> + at
            least one of{" "}
            <code className="rounded bg-blue-100 px-1">org_email</code>{" "}
            <code className="rounded bg-blue-100 px-1">personal_email</code>
          </p>
          <p className="mt-1 text-blue-600">
            Optional:{" "}
            <code className="rounded bg-blue-100 px-1">roll_number</code>{" "}
            <code className="rounded bg-blue-100 px-1">phone</code>
          </p>
        </div>

        {/* Doc type tabs */}
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Input area */}
        <div className="space-y-3">
          {!isGoogleSheet ? (
            <div className="space-y-2">
              <Label htmlFor="bulk-file">
                Upload {activeTab === "csv" ? "CSV" : "Excel"} file
              </Label>
              {!file ? (
                <Input
                  id="bulk-file"
                  ref={fileInputRef}
                  type="file"
                  accept={accept}
                  className="cursor-pointer"
                  value=""
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              ) : (
                <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
                  <div
                    className="flex flex-1 cursor-pointer items-center space-x-2 overflow-hidden text-orange-600 hover:underline"
                    onClick={() => {
                      const fileURL = URL.createObjectURL(file);
                      window.open(fileURL, "_blank");
                    }}
                    title="Click to preview file"
                  >
                    <FileText className="h-5 w-5 flex-shrink-0" />
                    <span className="truncate text-sm font-medium">
                      {file.name}
                    </span>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2 h-8 w-8 flex-shrink-0 rounded-full p-0 text-neutral-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => {
                      setFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    title="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="sheet-url">Google Sheet URL</Label>
              <Input
                id="sheet-url"
                type="url"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
              />
              {/* Google Sheet public warning */}
              <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                <AlertCircle
                  size={14}
                  className="mt-0.5 shrink-0 text-amber-500"
                />
                <div>
                  <p className="font-semibold">
                    Sheet must be publicly accessible
                  </p>
                  <p className="mt-0.5">
                    Go to <strong>Share → Anyone with the link → Viewer</strong>{" "}
                    before pasting the URL here.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Download sample */}
          {!isGoogleSheet && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-gray-500 hover:text-gray-700"
                onClick={downloadSampleCSV}
              >
                <Download size={13} />
                Download sample CSV
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-gray-500 hover:text-gray-700"
                onClick={downloadSampleExcel}
              >
                <FileSpreadsheet size={13} />
                Download sample Excel
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={uploadMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={handleSubmit}>
            {uploadMutation.isPending ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadStudentsDialog;
